<template>
  <div class="relative" :class="compact ? '' : 'h-[295px] rounded-xl'">
    <div
      v-if="!compact"
      class="absolute inset-0 rounded-xl border border-gray-800 overflow-hidden bg-gray-900"
      :style="backgroundImage ? `background-image: url('${backgroundImage}'); background-size: cover; background-position: center;` : ''"
    >
      <div class="absolute inset-0 image-gradient opacity-25" />
    </div>

    <p v-if="!compact" class="relative z-10 text-xs uppercase tracking-wide text-gray-300 px-6 pt-4">{{ $t('play.matchmaking.playlist') }}</p>

    <Menu as="div" class="z-50" :class="compact ? 'relative' : 'absolute bottom-2 left-2 right-2'">
      <MenuButton
        v-slot="{ open }"
        :disabled="disabled"
        class="w-full flex items-center justify-between rounded-md backdrop-blur-[1.5px] px-4 py-3 relative overflow-hidden"
        :class="[disabled ? 'cursor-not-allowed' : '', compact ? 'border border-gray-800 bg-gray-900 h-14' : '']"
        :style="compact && backgroundImage ? `background-image: linear-gradient(to right, rgba(15,19,26,0.55), rgba(15,19,26,0.9)), url('${backgroundImage}'); background-size: cover; background-position: center;` : ''"
      >
        <span class="font-semibold text-base" :class="disabled ? 'text-gray-400' : 'text-text-primary'">{{ modelValue?.friendly_name || $t('play.matchmaking.select_playlist') }}</span>
        <LockClosedIcon v-if="disabled" class="h-4 w-4 text-gray-500 flex-none" aria-hidden="true" />
        <ChevronDownIcon v-else class="h-5 w-5 text-gray-400 transition-transform" :class="open ? 'rotate-180' : ''" aria-hidden="true" />
      </MenuButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems
          class="absolute bottom-full left-0 z-50 mb-2 w-full origin-bottom rounded-lg border py-2 focus:outline-none"
          :class="compact ? 'min-w-[24rem]' : ''"
          style="background: #0F141A; border-color: #1E2733; box-shadow: 0px 8px 32px rgba(0,0,0,0.16);"
        >
          <MenuItem
            v-for="playMode in playModes"
            :key="playMode.id"
            @click="() => $emit('update:modelValue', playMode)"
            v-slot="{ active }"
          >
            <button
              class="w-full flex items-start px-2 py-2.5 gap-3 rounded transition-colors"
              :style="active ? 'background: rgba(255,255,255,0.05)' : ''"
            >
              <!-- radio indicator -->
              <span
                class="flex-shrink-0 w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center"
                :style="modelValue?.id === playMode.id
                  ? 'border-color: #6366f1; background: #6366f1'
                  : 'border-color: #374151; background: transparent'"
              >
                <span v-if="modelValue?.id === playMode.id" class="w-1.5 h-1.5 rounded-full bg-white" />
              </span>
              <span class="flex-1 min-w-0 flex flex-col gap-0.5 text-left">
                <span
                  class="text-sm font-medium"
                  :style="modelValue?.id === playMode.id ? 'color: #EFF2F5' : 'color: #9CADC4'"
                >{{ playMode.friendly_name }}</span>
                <span v-if="description(playMode)" class="text-xs leading-snug text-gray-500">{{ description(playMode) }}</span>
              </span>
              <span class="text-xs font-mono rounded px-1.5 py-0.5" style="color: #9CADC4; background: rgba(255,255,255,0.06);">{{ playModeBadge(playMode) }}</span>
            </button>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon, LockClosedIcon } from '@heroicons/vue/24/solid';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  playModes: { type: Array, default: () => [] },
  modelValue: { type: Object, default: null },
  // Locked while the party is queued, and for everyone who is not the leader.
  disabled: { type: Boolean, default: false },
  // Just the button with the drop-up list, for a toolbar.
  compact: { type: Boolean, default: false },
});
defineEmits(['update:modelValue']);
const { t, te } = useI18n();

const backgroundImage = computed(() => {
  const name = props.modelValue?.name;
  return name ? `/img/mods/${name.toLowerCase()}.webp` : null;
});

function description(playMode) {
  const key = `play.matchmaking.mode_descriptions.${playMode.name}`;
  return te(key) ? t(key) : null;
}

function playModeBadge(playMode) {
  if (playMode.players_per_team <= 2) return `${playMode.players_per_team}v${playMode.players_per_team}`;
  return t('play.matchmaking.max_party_size', { n: playMode.max_party_size });
}
</script>

<style scoped>
.image-gradient {
  background: linear-gradient(180deg, rgba(15, 19, 26, 0.9) 0%, #0f131a 50%, #0f131a 100%);
}
</style>
